import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  NumberInput,
  SegmentedControl,
  Divider,
  Center,
  Loader,
} from "@mantine/core";
import { IconGift, IconAlertCircle } from "@tabler/icons-react";
import { useApp } from "../../../../../context/AppContext";
import { useResponsive } from "../../../../../context/ResponsiveContext";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import {
  GiftCardConfig,
  GiftCardDeliveryChannel,
  SellGiftCardResponse,
} from "../../../../../types/giftCards";
import GiftCardCodeField from "../../../../common/GiftCardCodeField";
import GiftCardRecipientFields from "../../../../common/GiftCardRecipientFields";
import { ParsedPhoneNumber } from "../../../../common/StandardizedPhoneInput";

// Minimal email check for inline validation; the backend re-validates.
const _EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface GiftCardSaleModalProps {
  opened: boolean;
  onClose: () => void;
  /** Salon UUID (businessInfo.id) — routed to the /business-locations sell route. */
  salonId: string;
  /**
   * The purchaser (buyer) — REQUIRED. Every gift-card sale must attribute a
   * purchaser (the backend rejects a missing `client_id`); the Quick Sale flow
   * always has a client selected by the time this opens. Passed straight through
   * as the sale's `client_id`, and the Continue button is disabled without it.
   */
  purchaserClientId?: string | null;
  /** Purchaser display name, for a small confirmation line. */
  purchaserName?: string | null;
  /**
   * Hand the freshly-created UNPAID sale transaction back to the caller, which
   * then opens the normal checkout on it. The card is minted on payment.
   */
  onSold: (result: SellGiftCardResponse) => void;
}

/**
 * Sell a gift card: collect the face value (validated against the salon's
 * configured min/max) and create an unpaid sale transaction via `sellGiftCard`.
 * The caller opens checkout on the returned transaction; the card itself is
 * minted automatically once that transaction is paid.
 *
 * A dedicated flow (mirrors the Quick Sale package sub-flow), NOT a virtual
 * product — the sale is tax-exempt and earns no commission by construction on
 * the backend.
 */
export default function GiftCardSaleModal({
  opened,
  onClose,
  salonId,
  purchaserClientId,
  purchaserName,
  onSold,
}: GiftCardSaleModalProps) {
  const { isMobile } = useResponsive();
  const { getGiftCardConfig, sellGiftCard } = useApp();

  const [config, setConfig] = useState<GiftCardConfig | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [amount, setAmount] = useState<number | "">("");
  const [code, setCode] = useState("");
  const [codeValid, setCodeValid] = useState(true);
  // Staff either auto-generate the code or specify their own (Req 4). In
  // "custom" mode the code must pass validation before Continue enables.
  const [codeMode, setCodeMode] = useState<"auto" | "custom">("auto");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recipient delivery is always captured now (no opt-out toggle): every sold
  // card is emailed/texted to the recipient once the sale is paid (email + SMS
  // carry the code + message).
  const [channel, setChannel] = useState<GiftCardDeliveryChannel>("sms");
  const [recipientFirstName, setRecipientFirstName] = useState("");
  const [recipientLastName, setRecipientLastName] = useState("");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [parsedPhone, setParsedPhone] = useState<ParsedPhoneNumber | null>(
    null,
  );
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientMessage, setRecipientMessage] = useState("");

  // Reset the form and (re)load the salon's gift-card config each time the modal
  // opens, so the amount bounds always reflect the current settings.
  useEffect(() => {
    if (!opened) return;
    setAmount("");
    setCode("");
    setCodeValid(true);
    setCodeMode("auto");
    setError(null);
    setIsSubmitting(false);
    setConfig(null);
    setChannel("sms");
    setRecipientFirstName("");
    setRecipientLastName("");
    setPhoneDisplay("");
    setParsedPhone(null);
    setRecipientEmail("");
    setRecipientMessage("");
    let cancelled = false;
    (async () => {
      setIsLoadingConfig(true);
      try {
        const cfg = await getGiftCardConfig(salonId);
        if (!cancelled) setConfig(cfg);
      } catch (err) {
        console.error("Failed to load gift card config:", err);
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Failed to load gift card settings. Please try again.",
          );
        }
      } finally {
        if (!cancelled) setIsLoadingConfig(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, salonId, getGiftCardConfig]);

  const amountIsNumber = typeof amount === "number";
  const min = config?.min_amount ?? 0;
  const max = config?.max_amount ?? 0;
  const enabled = config?.is_enabled ?? false;

  const validationError: string | null = !amountIsNumber
    ? null
    : amount < min
      ? `Enter an amount of at least ${formatCurrency(min)}.`
      : max > 0 && amount > max
        ? `Enter an amount of at most ${formatCurrency(max)}.`
        : null;

  const wantsSms = channel === "sms" || channel === "both";
  const wantsEmail = channel === "email" || channel === "both";
  const emailValid = _EMAIL_RE.test(recipientEmail.trim());
  // When sending to a recipient, every field the chosen channel needs must be
  // filled: a phone for sms/both, a valid email for email/both.
  const recipientReady =
    (!wantsSms || parsedPhone !== null) && (!wantsEmail || emailValid);

  const canSubmit =
    enabled &&
    !!purchaserClientId &&
    amountIsNumber &&
    amount >= min &&
    (max <= 0 || amount <= max) &&
    !validationError &&
    (codeMode === "auto" || codeValid) &&
    recipientReady &&
    !isSubmitting &&
    !isLoadingConfig;

  const handleSubmit = async () => {
    if (!canSubmit || !amountIsNumber || !purchaserClientId) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await sellGiftCard(salonId, {
        amount,
        client_id: purchaserClientId,
        code: codeMode === "custom" ? code.trim() || undefined : undefined,
        delivery_channel: channel,
        recipient_first_name: recipientFirstName.trim() || undefined,
        recipient_last_name: recipientLastName.trim() || undefined,
        recipient_area_code: wantsSms ? parsedPhone?.areaCode : undefined,
        recipient_phone_number: wantsSms ? parsedPhone?.phoneNumber : undefined,
        recipient_email: wantsEmail ? recipientEmail.trim() : undefined,
        gift_message: recipientMessage.trim() || undefined,
      });
      onSold(result);
    } catch (err) {
      console.error("Error selling gift card:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to start the gift card sale. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Sell Gift Card</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {isLoadingConfig ? (
          <Center py="xl">
            <Loader color="var(--brand-teal)" />
          </Center>
        ) : !enabled && !error ? (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="yellow"
            variant="light"
          >
            Gift cards aren't enabled for this location yet. Turn them on in
            Settings → Gift Cards to sell one.
          </Alert>
        ) : (
          enabled && (
            <>
              {!purchaserClientId && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="yellow"
                  variant="light"
                >
                  Select a client before selling a gift card.
                </Alert>
              )}
              {purchaserName && (
                <Text size="sm" c="dimmed">
                  Purchaser: {purchaserName}
                </Text>
              )}
              <NumberInput
                label="Amount"
                placeholder="e.g. 50.00"
                value={amount}
                onChange={(value) =>
                  setAmount(typeof value === "number" ? value : "")
                }
                min={0}
                decimalScale={2}
                prefix="$"
                withAsterisk
                disabled={isSubmitting}
                error={validationError}
                description={
                  max > 0
                    ? `Between ${formatCurrency(min)} and ${formatCurrency(max)}.`
                    : `At least ${formatCurrency(min)}.`
                }
              />
              <Stack gap="xs">
                <SegmentedControl
                  fullWidth
                  value={codeMode}
                  onChange={(value) => {
                    const mode = value as "auto" | "custom";
                    setCodeMode(mode);
                    // Pessimistic until the field confirms a valid, free code.
                    if (mode === "custom") setCodeValid(false);
                  }}
                  disabled={isSubmitting}
                  data={[
                    { label: "Auto-generate a code", value: "auto" },
                    { label: "Specify a code", value: "custom" },
                  ]}
                />
                {codeMode === "custom" ? (
                  <GiftCardCodeField
                    salonId={salonId}
                    value={code}
                    onChange={setCode}
                    onValidityChange={setCodeValid}
                    disabled={isSubmitting}
                    required
                    label="Gift card code"
                  />
                ) : (
                  <Text size="xs" c="dimmed">
                    We&apos;ll generate a unique 8-character code automatically.
                  </Text>
                )}
              </Stack>
              <Divider label="Delivery" labelPosition="center" />
              <GiftCardRecipientFields
                channel={channel}
                onChannelChange={setChannel}
                firstName={recipientFirstName}
                onFirstNameChange={setRecipientFirstName}
                lastName={recipientLastName}
                onLastNameChange={setRecipientLastName}
                phoneDisplay={phoneDisplay}
                onPhoneDisplayChange={setPhoneDisplay}
                parsedPhone={parsedPhone}
                onParsedPhoneChange={setParsedPhone}
                email={recipientEmail}
                onEmailChange={setRecipientEmail}
                message={recipientMessage}
                onMessageChange={setRecipientMessage}
                emailValid={emailValid}
                disabled={isSubmitting}
              />
            </>
          )
        )}

        <Divider />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
            leftSection={<IconGift size={18} />}
          >
            Continue to Payment
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
